import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, IconButton, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { idCardService } from '../../../services/idCardService';
import type { IdCardAudience, IdCardDesign, IdCardDesignInput, IdCardLayout } from '../../../types/idCards';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import { AUDIENCE_LABEL, layoutOf, newDesign } from '../catalog';
import type { CardPerson } from '../cardPeople';
import type { CardSchool } from '../render/faces/shared';
import CardSidesPreview from '../CardSidesPreview';
import LayoutPicker from './LayoutPicker';
import PalettePicker from './PalettePicker';
import FieldPicker from './FieldPicker';
import BackSideSettings from './BackSideSettings';

interface Props {
  audience: IdCardAudience;
  /** The design to edit; null starts a new one. */
  design: IdCardDesign | null;
  school: CardSchool;
  validUntil: string | null;
  /** Who the preview shows, and their photo. */
  person: CardPerson;
  photo: string | null;
  onClose: () => void;
  onSaved: (design: IdCardDesign) => void;
}

const inputOf = (d: IdCardDesign): IdCardDesignInput => ({
  name: d.name, layout: d.layout, colors: d.colors, title: d.title, fields: d.fields, back: d.back,
});

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <Box sx={{ px: 3, py: 2.5, '& + &': { borderTop: 1, borderColor: 'divider' } }}>
    <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
    {hint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{hint}</Typography>}
    <Box sx={{ mt: 1.75 }}>{children}</Box>
  </Box>
);

/**
 * The design studio: settings on the left, the card — front and back,
 * drawn exactly as it prints — on the right, updating as you go.
 */
const DesignEditorDialog: React.FC<Props> = ({ audience, design, school, validUntil, person, photo, onClose, onSaved }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const initial = useMemo(() => (design ? inputOf(design) : newDesign(audience)), [design, audience]);
  const [form, setForm] = useState<IdCardDesignInput>(initial);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const layout = layoutOf(form.layout);
  const set = useCallback((patch: Partial<IdCardDesignInput>) => setForm(f => ({ ...f, ...patch })), []);
  // A layout with less room keeps the first details that fit.
  const setLayout = useCallback((key: IdCardLayout) => setForm(f => ({ ...f, layout: key, fields: f.fields.slice(0, layoutOf(key).maxFields) })), []);

  const face = useMemo(() => ({ design: { ...form, audience }, person, photo, school, validUntil }), [form, audience, person, photo, school, validUntil]);

  const problem = !form.name.trim() ? 'Give the design a name.'
    : !form.title.trim() ? 'Give the card a title.'
      : form.fields.length === 0 ? 'Choose at least one detail for the front.'
        : null;

  const save = async () => {
    if (problem) { enqueueSnackbar(problem, { variant: 'warning' }); return; }
    setSaving(true);
    try {
      const payload: IdCardDesignInput = {
        ...form,
        name: form.name.trim(),
        title: form.title.trim(),
        back: { ...form.back, instructions: form.back.instructions.map(l => l.trim()).filter(Boolean), signatory: form.back.signatory.trim() },
      };
      const saved = design ? await idCardService.updateDesign(design._id, payload) : await idCardService.createDesign(audience, payload);
      enqueueSnackbar(design ? 'Design saved' : `${saved.name} created${saved.isDefault ? ' — it is the default for printing' : ''}`, { variant: 'success' });
      onSaved(saved);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not save the design', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => { if (saving) return; if (dirty) setConfirmDiscard(true); else onClose(); };

  return (
    <Dialog fullScreen open onClose={requestClose} sx={{ zIndex: 1300 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        <IconButton onClick={requestClose} aria-label="Close"><Close /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} noWrap sx={{ lineHeight: 1.25 }}>
            {design ? form.name || 'Untitled design' : `New ${AUDIENCE_LABEL[audience].toLowerCase()} card design`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {AUDIENCE_LABEL[audience]} ID card · {layout.label} · {dirty ? 'Unsaved changes' : 'No changes'}
          </Typography>
        </Box>
        <Button onClick={requestClose} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={saving || (!!design && !dirty)} sx={{ textTransform: 'none', px: 2.5 }}>
          {saving ? 'Saving…' : design ? 'Save changes' : 'Create design'}
        </Button>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' } }}>
        {/* Settings */}
        <Box sx={{ width: { md: 440 }, flexShrink: 0, overflowY: 'auto', borderRight: { md: 1 }, borderColor: 'divider' }}>
          <Section title="Name">
            <Stack spacing={1.5}>
              <TextField size="small" label="Design name" value={form.name} onChange={e => set({ name: e.target.value.slice(0, 60) })}
                helperText="For your list only — it doesn't print." fullWidth />
              <TextField size="small" label="Card title" value={form.title} onChange={e => set({ title: e.target.value.slice(0, 40) })}
                helperText="Printed under the school's name." fullWidth />
            </Stack>
          </Section>
          <Section title="Layout">
            <LayoutPicker value={form.layout} onChange={setLayout} face={face} />
          </Section>
          <Section title="Colours" hint="Text colours adjust to stay readable on whatever you choose.">
            <PalettePicker value={form.colors} onChange={colors => set({ colors })} />
          </Section>
          <Section title="Details on the front" hint="Taken from each person's record when the cards print.">
            <FieldPicker
              audience={audience}
              value={form.fields}
              onChange={fields => set({ fields })}
              max={layout.maxFields}
              featuresFirst={form.layout === 'sidebar' || form.layout === 'portrait'}
              person={person}
            />
          </Section>
          <Section title="Back of the card">
            <BackSideSettings value={form.back} onChange={back => set({ back })} school={school} />
          </Section>
        </Box>

        {/* Canvas */}
        <Box
          sx={{
            flex: 1, minWidth: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            py: { xs: 3, md: 5 }, px: 3,
            bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.04 : 0.03),
            backgroundImage: `radial-gradient(${alpha(theme.palette.text.primary, 0.09)} 1px, transparent 1px)`,
            backgroundSize: '18px 18px',
          }}
        >
          <CardSidesPreview width={layout.orientation === 'portrait' ? 400 : 420} {...face} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 3 }}>
            Standard ID card size · 85.6 × 54 mm (CR80) · shown with {person.key === 'sample' ? 'sample details' : person.name}
          </Typography>
        </Box>
      </Box>

      <ConfirmationDialog
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => { setConfirmDiscard(false); onClose(); }}
        title="Discard your changes?"
        message="The design will stay as it was when you opened it."
        confirmText="Discard"
        confirmColor="error"
      />
    </Dialog>
  );
};

export default DesignEditorDialog;
